/**
 * One user account must hold either buyer access or distributor access, never
 * both: the apps pick an experience from the account, so a mixed account gets
 * an inconsistent one. These rules decide when the portal blocks a new link
 * and when it warns about an account that is already mixed.
 *
 * Linking a distributor (`userUpdate(supplierId)`) does not set the user's
 * `supplier` flag on the backend (see `handleLinkSupplier` in userService), so
 * linking an existing non-distributor user creates a linked-but-not-distributor
 * account. That is why the flag, not just the link, is checked.
 */

export interface UserEntityAccess {
  /** The user's `supplier` flag: the account was created as a distributor user. */
  isDistributorUser: boolean;
  linkedDistributorId?: string | null;
  linkedBuyerCount: number;
}

export interface UserAccessNotice {
  title: string;
  message: string;
}

const SEPARATE_USER_HINT =
  'If the email provider allows it, an alias such as name+buyer@company.com and name+distributor@company.com works.';

const BUYER_LINK_BLOCK: UserAccessNotice = {
  title: 'This user has distributor access',
  message: `A user with distributor access can't also be linked to a buyer. Create a separate user for buyer access. ${SEPARATE_USER_HINT}`,
};

const DISTRIBUTOR_LINK_BLOCK_HAS_BUYERS: UserAccessNotice = {
  title: 'This user is linked to buyers',
  message: `A user linked to buyers can't also be linked to a distributor. Create a separate user for distributor access. ${SEPARATE_USER_HINT}`,
};

const DISTRIBUTOR_LINK_BLOCK_NOT_DISTRIBUTOR: UserAccessNotice = {
  title: 'This user is not a distributor user',
  message:
    'Tick Distributor under User Type and save, then link the distributor.',
};

const MIXED_ACCESS_WARNING: UserAccessNotice = {
  title: 'This user has both buyer and distributor access',
  message:
    'One user with both gets an inconsistent experience. Keep one kind of access on this user and create a separate user for the other.',
};

const LINKED_NOT_DISTRIBUTOR_WARNING: UserAccessNotice = {
  title: 'Linked to a distributor, but not a distributor user',
  message:
    'This user may not get the distributor experience. Tick Distributor under User Type and save.',
};

const hasDistributorAccess = (access: UserEntityAccess): boolean =>
  access.isDistributorUser || !!access.linkedDistributorId;

/** Why this user can't be linked to a buyer, or null when they can. */
export const getBuyerLinkBlock = (
  access: UserEntityAccess
): UserAccessNotice | null =>
  hasDistributorAccess(access) ? BUYER_LINK_BLOCK : null;

/**
 * Why this user can't be linked to a distributor, or null when they can.
 * `isNewUser`: the create form sets the distributor flag together with the
 * link, so a user being created does not need the flag already.
 */
export const getDistributorLinkBlock = (
  access: UserEntityAccess,
  { isNewUser = false }: { isNewUser?: boolean } = {}
): UserAccessNotice | null => {
  if (access.linkedBuyerCount > 0) return DISTRIBUTOR_LINK_BLOCK_HAS_BUYERS;
  if (!isNewUser && !access.isDistributorUser) {
    return DISTRIBUTOR_LINK_BLOCK_NOT_DISTRIBUTOR;
  }
  return null;
};

/** Problems with links the user already has. */
export const getUserAccessWarnings = (
  access: UserEntityAccess
): UserAccessNotice[] => {
  const warnings: UserAccessNotice[] = [];
  if (hasDistributorAccess(access) && access.linkedBuyerCount > 0) {
    warnings.push(MIXED_ACCESS_WARNING);
  }
  if (access.linkedDistributorId && !access.isDistributorUser) {
    warnings.push(LINKED_NOT_DISTRIBUTOR_WARNING);
  }
  return warnings;
};
